import React, { useState, useReducer, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import Swal from 'sweetalert2';
import { getCessionInformation, uploadCessionDocument, setCessionStatus, fileToBase64 } from '../../services/cesionesService';
import { getUserSession } from '../../utils/auth';

const Campo = React.memo(({ label, value }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    <div className="w-full px-3 py-2 border border-gray-200 rounded-md text-gray-800 text-sm bg-gray-50 min-h-[34px] flex items-center">
      {value || '—'}
    </div>
  </div>
));

const Tarjeta = React.memo(({ titulo, icono, children }) => (
  <div className="bg-white border-2 border-gray-100 rounded-2xl shadow-soft hover:shadow-xl transition-all duration-300 overflow-hidden group">
    <div className="h-1.5 gradient-red group-hover:h-2 transition-all duration-300"></div>
    <div className="bg-gradient-to-br from-honda-50 to-white px-6 py-4 border-b border-gray-100">
      <h3 className="text-sm font-bold text-honda-800 uppercase tracking-wide flex items-center gap-2">
        <span className="material-icons text-honda-600">{icono}</span>
        {titulo}
      </h3>
    </div>
    <div className="p-4">{children}</div>
  </div>
));

const FileUploadRow = React.memo(({ idx, doc, file, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {doc.documentName}
      {idx === 0 && <span className="text-red-500 ml-1">*</span>}
    </label>
    <div className="relative">
      <input type="file" id={`doc-${idx}`} className="hidden" accept=".pdf"
        onChange={e => { if (e.target.files?.[0]) onChange(idx, e.target.files[0]); }}
      />
      <label htmlFor={`doc-${idx}`}
        className={`flex items-center justify-between w-full px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 ${file ? 'bg-green-50 border-green-300 hover:bg-green-100' : 'bg-gray-50 border-gray-300 hover:bg-gray-100 hover:border-red-400'}`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className={`material-icons text-xl ${file ? 'text-green-600' : 'text-gray-400'}`}>
            {file ? 'check_circle' : 'cloud_upload'}
          </span>
          <span className="text-sm text-gray-600 truncate">{file ? file.name : 'Seleccionar archivo...'}</span>
        </div>
        {file && (
          <button type="button" onClick={e => { e.preventDefault(); e.stopPropagation(); onChange(idx, null); }}
            className="ml-2 text-red-500 hover:text-red-700 transition-colors"
          >
            <span className="material-icons text-sm">close</span>
          </button>
        )}
      </label>
    </div>
  </div>
));

const DOCUMENTOS_REQUERIDOS = [
  { documentName: 'CESION + ACTUACION NOTARIAL' },
  { documentName: 'DNI/CUIT DEL COMPRADOR' },
  { documentName: 'DNI/CUIT DEL VENDEDOR' },
];

const initialState = {
  grupo: '', orden: '', cessionID: '',
  fase: 'busqueda', contrato: null, archivos: {},
  loading: false, enviando: false,
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD': return { ...state, [action.field]: action.value };
    case 'SET_LOADING': return { ...state, loading: action.value };
    case 'SET_ENVIANDO': return { ...state, enviando: action.value };
    case 'SET_CONTRATO': return { ...state, contrato: action.contrato, fase: 'preview', loading: false };
    case 'AVANZAR_DETALLES': return { ...state, fase: 'detalles' };
    case 'SET_ARCHIVO': return { ...state, archivos: { ...state.archivos, [action.idx]: action.file } };
    case 'PRECARGAR_DETALLES': return { ...state, contrato: action.contrato, fase: 'detalles', loading: false };
    case 'RESET': return { ...initialState };
    default: return state;
  }
}

const IniciarCesion = ({ user, onLogout, onNavigate }) => {
  const location = useLocation();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { grupo, orden, cessionID, fase, contrato, archivos, loading, enviando } = state;

  // Si se navega con cessionData pre-cargada, saltar directamente a la fase detalles
  useEffect(() => {
    const navData = location.state;
    if (!navData?.cessionData) return;
    const data = navData.cessionData;
    if (!data.objContract) return;
    dispatch({
      type: 'PRECARGAR_DETALLES',
      contrato: {
        cessionID: data.cessionID,
        grupo: data.objContract.group,
        orden: data.objContract.order,
        tipoFormulario: data.objContract.formType,
        personType: data.personType || data.objBuyer?.personType || '',
        comprador: {
          nombre: data.objBuyer?.fullName, dni: data.objBuyer?.ident,
          telefono: data.objBuyer?.phone, email: data.objBuyer?.email, tipo: data.objBuyer?.personType,
        },
        vendedor: {
          nombre: data.objSeller?.fullName, dni: data.objSeller?.ident,
          telefono: data.objSeller?.phone, email: data.objSeller?.email, tipo: data.objSeller?.personType,
        },
        documentos: data.documents || [],
      },
    });
  }, [location.state]);

  const buscarOperacion = useCallback(async () => {
    if (!grupo && !orden && !cessionID) {
      await Swal.fire({ icon: 'warning', title: 'Campos requeridos', text: 'Ingrese grupo, orden o Cession ID.', confirmButtonColor: '#ef4444' });
      return;
    }
    dispatch({ type: 'SET_LOADING', value: true });
    try {
      const data = await getCessionInformation({ group: grupo, order: orden, cessionID, sellerID: 0 });
      if (!data || !data.objContract) {
        await Swal.fire({ icon: 'info', title: 'Sin resultados', text: 'No se encontró la cesión.', confirmButtonColor: '#ef4444' });
        dispatch({ type: 'SET_LOADING', value: false });
        return;
      }
      const estadoActual = (data.cessionStatus || '').trim().toUpperCase();
      const estadosPermitidos = ['', "PRECESION"];
      if (estadoActual && !estadosPermitidos.includes(estadoActual)) {
        await Swal.fire({
          icon: 'warning',
          title: 'Caso no disponible',
          text: `Esta cesión se encuentra en estado "${data.cessionStatus}" y no puede ser iniciada desde aquí.`,
          confirmButtonColor: '#ef4444',
        });
        dispatch({ type: 'SET_LOADING', value: false });
        return;
      }
      dispatch({
        type: 'SET_CONTRATO',
        contrato: {
          cessionID: data.cessionID,
          grupo: data.objContract.group,
          orden: data.objContract.order,
          tipoFormulario: data.objContract.formType,
          personType: data.personType || data.objBuyer?.personType || '',
          comprador: {
            nombre: data.objBuyer?.fullName, dni: data.objBuyer?.ident,
            telefono: data.objBuyer?.phone, email: data.objBuyer?.email, tipo: data.objBuyer?.personType,
          },
          vendedor: {
            nombre: data.objSeller?.fullName, dni: data.objSeller?.ident,
            telefono: data.objSeller?.phone, email: data.objSeller?.email, tipo: data.objSeller?.personType,
          },
          documentos: data.documents || [],
        },
      });
    } catch (err) {
      await Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'No se pudo consultar la cesión.', confirmButtonColor: '#ef4444' });
      dispatch({ type: 'SET_LOADING', value: false });
    }
  }, [grupo, orden, cessionID]);

  const handleNuevaBusqueda = useCallback(() => dispatch({ type: 'RESET' }), []);
  const handleFileChange = useCallback((idx, file) => dispatch({ type: 'SET_ARCHIVO', idx, file }), []);
  const handleKeyDown = useCallback(e => { if (e.key === 'Enter') buscarOperacion(); }, [buscarOperacion]);

  const handleEnviarCesion = useCallback(async () => {
    const documentos = DOCUMENTOS_REQUERIDOS;
    if (!archivos[0]) {
      await Swal.fire({ icon: 'warning', title: 'Documento requerido', text: 'Cargue al menos el primer documento.', confirmButtonColor: '#ef4444' });
      return;
    }
    const confirm = await Swal.fire({
      title: '¿Enviar a revisión?',
      html: `
    <div style="font-size:15px;">
      <strong>Grupo ${contrato.grupo} · Orden ${contrato.orden}</strong>
      <br><br>
      Una vez enviado, el expediente quedará disponible para su evaluación.
    </div>
  `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#6b7280',
      confirmButtonText: '<i class="fas fa-paper-plane"></i> Enviar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      focusCancel: true
    });
    if (!confirm.isConfirmed) return;
    dispatch({ type: 'SET_ENVIANDO', value: true });
    try {
      const session = getUserSession();
      const userID = session?.id || 0;
      await Promise.all(documentos.map(async (doc, idx) => {
        const file = archivos[idx];
        if (!file) return null;
        const base64 = await fileToBase64(file);
        return uploadCessionDocument({ idCession: contrato.cessionID, documentName: doc.documentName, documentB64: base64 });
      }));
      await setCessionStatus({ idCession: contrato.cessionID, cessionStatus: 'EN REVISION', statusNotes: 'Cesion enviada a revision por concesionario', userID });
      await Swal.fire({ title: 'Cesion enviada!', text: 'Expediente enviado correctamente a revision.', icon: 'success', confirmButtonColor: '#ef4444' });
      onNavigate('home');
    } catch (err) {
      await Swal.fire({ icon: 'error', title: 'Error al enviar', text: err.message || 'No se pudo enviar la cesion.', confirmButtonColor: '#ef4444' });
    } finally {
      dispatch({ type: 'SET_ENVIANDO', value: false });
    }
  }, [contrato, archivos, onNavigate]);

  const isPersonaJuridica = contrato?.personType?.includes('2') || contrato?.comprador?.tipo?.includes('2');

  return (
    <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
      <div className="max-w-screen-xl mx-auto px-6 py-6 bg-gray-50 min-h-screen">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>
            <h2 className="text-2xl font-bold text-gradient">Iniciar Cesion</h2>
          </div>
          <p className="text-gray-500 text-sm ml-5">Gestion de transferencias de planes de ahorro</p>
        </div>

        {(fase === 'busqueda' || fase === 'preview') && (
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Grupo</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center"><span className="material-icons text-gray-400 text-base">tag</span></span>
                    <input type="text" placeholder="Ej: 010982" value={grupo}
                      onChange={e => dispatch({ type: 'SET_FIELD', field: 'grupo', value: e.target.value })}
                      onKeyDown={handleKeyDown}
                      className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-md text-gray-700 text-sm focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Orden</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center"><span className="material-icons text-gray-400 text-base">receipt_long</span></span>
                    <input type="text" placeholder="Ej: 0106" value={orden}
                      onChange={e => dispatch({ type: 'SET_FIELD', field: 'orden', value: e.target.value })}
                      onKeyDown={handleKeyDown}
                      className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-md text-gray-700 text-sm focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Cession ID</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center"><span className="material-icons text-gray-400 text-base">confirmation_number</span></span>
                    <input type="text" placeholder="Ej: AP-28882" value={cessionID}
                      onChange={e => dispatch({ type: 'SET_FIELD', field: 'cessionID', value: e.target.value })}
                      onKeyDown={handleKeyDown}
                      className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-md text-gray-700 text-sm focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    />
                  </div>
                </div>
                <button onClick={buscarOperacion} disabled={loading}
                  className="w-full px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-md transition-all text-sm font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <span className="material-icons text-base">{loading ? 'hourglass_empty' : 'search'}</span>
                  {loading ? 'Buscando...' : 'Buscar Operacion'}
                </button>
              </div>
            </div>

            {loading && (
              <div className="bg-white border-2 border-gray-100 rounded-2xl shadow-soft p-8">
                <HondaLoader variant="inline" message="Consultando informacion..." />
              </div>
            )}

            {fase === 'preview' && contrato && !loading && (
              <div className="space-y-4 animate-fade-in">
                <Tarjeta titulo="Informacion del Contrato" icono="description">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Campo label="Cession ID" value={contrato.cessionID} />
                    <Campo label="Grupo" value={contrato.grupo} />
                    <Campo label="Orden" value={contrato.orden} />
                    <Campo label="Tipo de Formulario" value={contrato.tipoFormulario} />
                  </div>
                </Tarjeta>
                <Tarjeta titulo="Comprador" icono="person_add">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <div className="md:col-span-2"><Campo label="Nombre completo" value={contrato.comprador.nombre} /></div>
                    <Campo label="DNI / CUIT" value={contrato.comprador.dni} />
                  </div>
                </Tarjeta>
                <Tarjeta titulo="Vendedor" icono="person_remove">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <div className="md:col-span-2"><Campo label="Nombre completo" value={contrato.vendedor.nombre} /></div>
                    <Campo label="DNI / CUIT" value={contrato.vendedor.dni} />
                  </div>
                </Tarjeta>
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={handleNuevaBusqueda} className="px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 transition-all text-sm font-medium flex items-center gap-2">
                    <span className="material-icons text-base">close</span>Cancelar
                  </button>
                  <button onClick={() => dispatch({ type: 'AVANZAR_DETALLES' })} className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl font-semibold text-sm shadow-lg shadow-red-200 hover:shadow-xl transition-all transform hover:scale-105 active:scale-95 flex items-center gap-2">
                    <span className="material-icons text-base">check_circle</span>Aceptar y continuar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {fase === 'detalles' && contrato && (
          <div className="space-y-4 animate-fade-in">
            <Tarjeta titulo="Informacion del Contrato" icono="description">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Campo label="Grupo" value={contrato.grupo} />
                <Campo label="Orden" value={contrato.orden} />
                <Campo label="Cession ID" value={contrato.cessionID} />
                <Campo label="Tipo de Formulario" value={contrato.tipoFormulario} />
              </div>
            </Tarjeta>
            <Tarjeta titulo="Datos del Comprador" icono="person_add">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="md:col-span-2"><Campo label="Nombre Completo / Razon Social" value={contrato.comprador.nombre} /></div>
                <Campo label="DNI / CUIT" value={contrato.comprador.dni} />
                <Campo label="Tipo de Persona" value={contrato.comprador.tipo} />
                <div className="md:col-span-2"><Campo label="Email" value={contrato.comprador.email} /></div>
                <Campo label="Telefono" value={contrato.comprador.telefono} />
              </div>
            </Tarjeta>
            <Tarjeta titulo="Datos del Vendedor" icono="person_remove">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="md:col-span-2"><Campo label="Nombre Completo / Razon Social" value={contrato.vendedor.nombre} /></div>
                <Campo label="DNI / CUIT" value={contrato.vendedor.dni} />
                <Campo label="Tipo de Persona" value={contrato.vendedor.tipo} />
                <div className="md:col-span-2"><Campo label="Email" value={contrato.vendedor.email} /></div>
                <Campo label="Telefono" value={contrato.vendedor.telefono} />
              </div>
            </Tarjeta>
            <Tarjeta titulo="Documentacion Requerida" icono="folder_open">
              {isPersonaJuridica && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                  <span className="material-icons text-blue-600 text-sm mt-0.5">business</span>
                  <p className="text-xs text-blue-700">Persona Juridica: Adjunte la documentacion societaria requerida.</p>
                </div>
              )}
              <div className="space-y-3">
                {DOCUMENTOS_REQUERIDOS.map((doc, idx) => (
                  <FileUploadRow key={idx} idx={idx} doc={doc} file={archivos[idx]} onChange={handleFileChange} />
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-4">* Documentos obligatorios para enviar a revision</p>
            </Tarjeta>
            <div className="flex justify-end gap-3 pt-2 pb-4">
              <button onClick={handleNuevaBusqueda} disabled={enviando} className="px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 transition-all text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                <span className="material-icons text-base">close</span>Cancelar
              </button>
              <button onClick={handleEnviarCesion} disabled={enviando} className="px-8 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl font-semibold text-sm shadow-lg shadow-red-200 hover:shadow-xl transition-all transform hover:scale-105 active:scale-95 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                <span className="material-icons text-base">{enviando ? 'hourglass_empty' : 'send'}</span>
                {enviando ? 'Enviando...' : 'Enviar a Revision'}
              </button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default IniciarCesion;